import {Component, OnDestroy, OnInit} from "@angular/core";
import {MatDialog} from "@angular/material/dialog";
import {
  AppExercisesCategorySelectDialogComponent,
  IExerciseTitle,
  IMostActiveExercise
} from "./app-exercises-category-select-dialog/app-exercises-category-select-dialog.component";
import {ExercisesControllerService} from "../../../core/api/controllers/exercises-controller/exercises-controller.service";
import {IGetExercisesLeaderboardReq} from "../../../shared/interfaces/DTOs/Request/IGetExercisesLeaderboardReq";
import {ILeaderboardRow} from "../../../shared/interfaces/DTOs/Response/IGetExercisesLeaderboardRes";
import {ETimeFrame} from "../../../shared/enums/ETimeFrame";
import {FormControl} from "@angular/forms";
import {ESortDirection} from "../../../shared/enums/ESortDirection";
import {UsersCacheStorageService} from "../../../core/services/users-cache-storage.service";
import {GlobalPreferences} from "../../../core/helpers/global-data";
import {_pt} from "../../../core/services/preview-text/helpers/preview-text.function";
import {Subscription} from "rxjs";


@Component({
  selector: "app-most-active-exercise-categories",
  templateUrl: "./most-active-exercise-categories.component.html",
  host: {
    class: "w-full",
  },
  styleUrls: ["./most-active-exercise-categories.component.scss"],
})
export class MostActiveExerciseCategoriesComponent implements OnInit, OnDestroy{
  isLoading = false;
  leaderboardRows: ILeaderboardRow[] = [];
  timeFrame = new FormControl<ETimeFrame>(ETimeFrame.AllTime);
  payload: IGetExercisesLeaderboardReq;
  selectedExerciseTitle: IExerciseTitle;
  sortDirection = ESortDirection.DESC;
  sortKey = "best";
  todayTimeRange: Partial<IGetExercisesLeaderboardReq> = null;
  lastMonthTimeRange: Partial<IGetExercisesLeaderboardReq> = null;
  lastWeekTimeRange: Partial<IGetExercisesLeaderboardReq> = null;
  allTimeRange: Partial<IGetExercisesLeaderboardReq> = null;

  cachedLeaderboard: Subscription;

  constructor(
      private dialog: MatDialog,
      private exercisesController: ExercisesControllerService,
      private userCacheService: UsersCacheStorageService,
      public global: GlobalPreferences,
  ) {}

  ngOnInit(): void {
    this.todayTimeRange = this.getTimeRange(ETimeFrame.Today);
    this.lastMonthTimeRange = this.getTimeRange(ETimeFrame.LastMonth);
    this.lastWeekTimeRange = this.getTimeRange(ETimeFrame.LastWeek);
    this.allTimeRange = this.getTimeRange(ETimeFrame.AllTime);
    this.subscribeToCachedLeaderboard();
  }

  ngOnDestroy(): void {
    this.unsubscribeToCachedLeaderboard();
  }

  subscribeToCachedLeaderboard(): void {
    this.cachedLeaderboard = this.userCacheService.currentCachedLocationObservable.subscribe(currentCachedUser => {
      if(currentCachedUser?.leaderboardExercise) {
        this.payload = { configId: currentCachedUser?.leaderboardExercise.configId, ...this.allTimeRange, gmtOffset: this.global.getTimezoneOffset()} as IGetExercisesLeaderboardReq;
        this.selectedExerciseTitle = currentCachedUser.leaderboardExercise.title;
        this.setTimeRange();
      }
    })
  }

  unsubscribeToCachedLeaderboard(): void {
    this.cachedLeaderboard.unsubscribe();
  }

  getLeaderboard(payload: IGetExercisesLeaderboardReq): void {
    this.isLoading = true;
    this.exercisesController.getLeaderboard(payload).subscribe({
      next: (res) => {
        this.leaderboardRows = res;
        this.isLoading = false;
        this.userCacheService.setLeaderboardExerciseConfig(payload.configId, this.selectedExerciseTitle);
      }
    })
  }

  toggleSort(key: string): void {
    if(key === this.sortKey) this.sortDirection = this.sortDirection === ESortDirection.DESC ? ESortDirection.ASC : ESortDirection.DESC;
    else {
      this.sortKey = key;
      this.sortDirection = ESortDirection.DESC;
    }
    this.sortTable(this.sortKey, this.sortDirection);
  }

  sortTable(key: string, direction: ESortDirection): void {
    if(direction === ESortDirection.ASC) {
      this.leaderboardRows = this.leaderboardRows.sort((a, b) => {
        if(typeof a[key] === 'number') return a[key] - b[key];
        else return a[key].localeCompare(b[key]);
      });
    } else {
      this.leaderboardRows = this.leaderboardRows.sort((a, b) => {
        if(typeof a[key] === 'number') return b[key] - a[key];
        else return b[key]?.localeCompare(a[key]);
      });
    }
  }

  openSelectExerciseConfigModal(): void {
    this.dialog.open(AppExercisesCategorySelectDialogComponent, {
      maxHeight: '90vh',
      height: '90vh',
    }).afterClosed().subscribe({
      next:(res: IMostActiveExercise) => {
        this.payload = {
          ...res.payload,
          ...this.timeRange
        }
        this.selectedExerciseTitle = res.exerciseTitle;
        this.getLeaderboard(this.payload);
      }
    });
  }

  setTimeRange(): void {
    this.payload = {
      ...this.payload,
      ...this.timeRange
    }
    if(this.payload.configId) {
      this.getLeaderboard(this.payload);
    }
  }

  get timeRange(): Partial<IGetExercisesLeaderboardReq> {
    return this.getTimeRange(this.timeFrame.value);
  }

  getTimeRange(timeFrame: ETimeFrame): Partial<IGetExercisesLeaderboardReq> {
    let today = new Date();
    let end: Date;
    today.setHours(23, 59, 59, 999);
    let start = new Date();
    switch (timeFrame) {
      case ETimeFrame.Today: {
        start = new Date(today);
        end = new Date(today);
        start.setHours(0, 0 , 0 , 0);
        break;
      }
      case ETimeFrame.AllTime: {
        start = new Date(today.getFullYear() - 15);
        end = new Date(today);
        break;
      }
      case ETimeFrame.LastMonth: {
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        break;
      }
      case ETimeFrame.LastWeek: {
        const daysInWeek = today.getDay() - 1;
        start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7 - daysInWeek);
        end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - daysInWeek);
        break;
      }
    }

    return {
      start: start.toISOString(),
      end: end.toISOString()
    }
  }

  protected readonly ETimeFilter = ETimeFrame;
    protected readonly _pt = _pt;
}
